/* =====================================================
   AUTOCARE PRO — THEME JS
   Vanilla ES6+ | No external dependencies
   ===================================================== */

'use strict';

/* ─── CART STATE ─────────────────────────────────── */
let __cartData = null;
let __modalVariantId = null;
let __modalInventory = 99;

/* ─── MONEY FORMAT ───────────────────────────────── */
function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

/* ─── TOAST NOTIFICATIONS ────────────────────────── */
function showToast(message, type = 'success') {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.setAttribute('role', 'alert');
  toast.innerHTML = `
    <span class="toast__icon" aria-hidden="true">${icons[type] || '✓'}</span>
    <span class="toast__message">${message}</span>
  `;

  container.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('is-leaving');
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
  }, 3000);
}

/* ─── ANNOUNCEMENT BAR ───────────────────────────── */
function dismissAnnouncement() {
  const bar = document.getElementById('announcement-bar');
  if (!bar) return;
  bar.classList.add('is-dismissed');
  try { sessionStorage.setItem('acp_announce_dismissed', '1'); } catch (_) {}
}

function initAnnouncementBar() {
  try {
    if (sessionStorage.getItem('acp_announce_dismissed') === '1') {
      const bar = document.getElementById('announcement-bar');
      if (bar) bar.classList.add('is-dismissed');
    }
  } catch (_) {}
}

/* ─── OVERLAY ────────────────────────────────────── */
function showOverlay() {
  const overlay = document.getElementById('overlay');
  if (overlay) overlay.classList.add('is-visible');
  document.body.style.overflow = 'hidden';
}

function hideOverlay() {
  const overlay = document.getElementById('overlay');
  if (overlay) overlay.classList.remove('is-visible');
  document.body.style.overflow = '';
}

function closeAll() {
  closeCartDrawer();
  closeMobileMenu();
  closeProductModal();
}

/* ─── MOBILE MENU ─────────────────────────────────── */
function toggleMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  const btn  = document.getElementById('hamburger-btn');
  if (!menu) return;
  const isOpen = !menu.hidden;
  if (isOpen) { closeMobileMenu(); } else { openMobileMenu(); }
}

function openMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  const btn  = document.getElementById('hamburger-btn');
  if (!menu) return;

  menu.hidden = false;
  requestAnimationFrame(() => menu.classList.add('is-open'));
  if (btn) { btn.setAttribute('aria-expanded', 'true'); }
  showOverlay();

  const focusable = menu.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])');
  if (focusable.length) focusable[0].focus();

  menu.addEventListener('keydown', trapMobileMenuFocus);
}

function closeMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  const btn  = document.getElementById('hamburger-btn');
  if (!menu) return;

  menu.classList.remove('is-open');
  menu.hidden = true;
  if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.focus(); }
  hideOverlay();
  menu.removeEventListener('keydown', trapMobileMenuFocus);
}

function trapMobileMenuFocus(e) {
  if (e.key !== 'Tab') {
    if (e.key === 'Escape') { closeMobileMenu(); return; }
    return;
  }
  const menu     = document.getElementById('mobile-menu');
  const focusable = Array.from(menu.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])')).filter(el => !el.disabled);
  const first    = focusable[0];
  const last     = focusable[focusable.length - 1];

  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault(); last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault(); first.focus();
  }
}

/* ─── CART API ────────────────────────────────────── */
async function addToShopifyCart(variantId, quantity = 1) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }

    const data = await res.json();
    await updateCartCount();
    showToast(`${data.title} added to cart!`, 'success');
    return data;
  } catch (err) {
    showToast(err.message || 'Something went wrong. Please try again.', 'error');
    throw err;
  }
}

async function removeFromCart(key) {
  return updateCartQuantity(key, 0);
}

async function updateCartQuantity(key, quantity) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    });

    if (!res.ok) throw new Error('Could not update cart');

    const cart = await res.json();
    __cartData = cart;
    renderCartDrawer(cart);
    updateCartCountFromData(cart);

    if (quantity === 0) showToast('Item removed from cart.', 'info');
    return cart;
  } catch (err) {
    showToast(err.message || 'Could not update cart. Try again.', 'error');
    throw err;
  }
}

async function updateCartCount() {
  try {
    const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await res.json();
    __cartData = cart;
    updateCartCountFromData(cart);
    return cart;
  } catch (_) {}
}

function updateCartCountFromData(cart) {
  const count = cart.item_count || 0;
  const badge = document.getElementById('cart-count');
  if (badge) badge.textContent = count;

  const cartToggle = document.getElementById('cart-toggle');
  if (cartToggle) cartToggle.setAttribute('aria-label', `Open cart, ${count} item${count !== 1 ? 's' : ''}`);
}

async function addBundleToCart() {
  const ids = window.BUNDLE_VARIANT_IDS;
  if (!ids || !ids.length) {
    showToast('Bundle products not configured yet.', 'warning');
    return;
  }

  const btn      = document.getElementById('bundle-add-btn');
  const textEl   = btn && btn.querySelector('.bundle-btn-text');
  const loadEl   = btn && btn.querySelector('.bundle-btn-loading');
  const succEl   = btn && btn.querySelector('.bundle-btn-success');

  if (btn) {
    btn.disabled = true;
    if (textEl) textEl.style.display = 'none';
    if (loadEl) loadEl.style.display = 'inline-flex';
  }

  try {
    const items = ids.map(id => ({ id: parseInt(id, 10), quantity: 1 }));
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add bundle');
    }

    await updateCartCount();
    showToast('Full Car Clean Kit added to cart!', 'success');

    if (btn) {
      if (loadEl) loadEl.style.display = 'none';
      if (succEl) { succEl.style.display = 'inline-flex'; btn.classList.add('is-success'); }
      setTimeout(() => {
        if (textEl) textEl.style.display = 'inline-flex';
        if (succEl) succEl.style.display = 'none';
        btn.classList.remove('is-success');
        btn.disabled = false;
      }, 3000);
    }
  } catch (err) {
    showToast(err.message || 'Could not add bundle. Try again.', 'error');
    if (btn) {
      if (loadEl) loadEl.style.display = 'none';
      if (textEl) textEl.style.display = 'inline-flex';
      btn.disabled = false;
    }
  }
}

/* ─── CART DRAWER ─────────────────────────────────── */
function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  fetchAndRenderCart().then(() => {
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    showOverlay();
    const close = drawer.querySelector('.cart-drawer__close');
    if (close) close.focus();
    document.addEventListener('keydown', handleCartEsc);
  });
}

function closeCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.classList.remove('is-open');
  drawer.hidden = true;
  hideOverlay();
  document.removeEventListener('keydown', handleCartEsc);
  const toggle = document.getElementById('cart-toggle');
  if (toggle) toggle.focus();
}

function handleCartEsc(e) {
  if (e.key === 'Escape') closeCartDrawer();
}

async function fetchAndRenderCart() {
  try {
    const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await res.json();
    __cartData = cart;
    renderCartDrawer(cart);
    updateCartCountFromData(cart);
  } catch (_) {}
}

function renderCartDrawer(cart) {
  const body   = document.getElementById('cart-drawer-body');
  const footer = document.getElementById('cart-drawer-footer');
  const count  = document.getElementById('cart-drawer-count');

  if (count) count.textContent = `(${cart.item_count})`;
  if (!body)  return;

  if (cart.item_count === 0) {
    body.innerHTML = `
      <div class="cart-empty">
        <div class="cart-empty__icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
        </div>
        <p class="cart-empty__title">Your cart is empty</p>
        <p class="cart-empty__sub">Add some products to get started</p>
        <a href="/collections/all" class="btn btn-primary" onclick="closeCartDrawer()">Shop Now →</a>
      </div>`;
    if (footer) footer.innerHTML = '';
    return;
  }

  const itemsHtml = cart.items.map(item => {
    const safeKey = item.key.replace(/:/g, '-');
    const imgHtml = item.image
      ? `<img src="${item.image.replace('.jpg', '_160x160_crop_center.jpg')}" alt="${escapeHtml(item.title)}" loading="lazy" width="80" height="80">`
      : '<div class="cart-item__no-image"></div>';
    const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
      ? `<div class="cart-item__variant">${escapeHtml(item.variant_title)}</div>` : '';

    return `
      <div class="cart-item" data-key="${item.key}" id="cart-item-${safeKey}">
        <div class="cart-item__image"><a href="${item.url}">${imgHtml}</a></div>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__name">${escapeHtml(item.title)}</a>
          ${variantHtml}
          <div class="cart-item__unit-price">${formatMoney(item.price)} each</div>
          <div class="cart-item__controls">
            <div class="qty-selector qty-selector--sm">
              <button class="qty-btn" onclick="updateCartQuantity('${item.key}', ${item.quantity - 1})" type="button" aria-label="Decrease quantity">−</button>
              <span class="qty-display">${item.quantity}</span>
              <button class="qty-btn" onclick="updateCartQuantity('${item.key}', ${item.quantity + 1})" type="button" aria-label="Increase quantity">+</button>
            </div>
            <button class="cart-item__remove" onclick="removeFromCart('${item.key}')" type="button" aria-label="Remove ${escapeHtml(item.title)}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            </button>
          </div>
        </div>
        <div class="cart-item__line-price">${formatMoney(item.line_price)}</div>
      </div>`;
  }).join('');

  body.innerHTML = `<div class="cart-items">${itemsHtml}</div>`;

  const threshold = 3500;
  const progress  = Math.min(100, Math.round((cart.total_price / threshold) * 100));
  const shippingHtml = cart.total_price < threshold
    ? `<div class="cart-free-shipping-bar">
        <div class="cfb-text">Add <strong>${formatMoney(threshold - cart.total_price)}</strong> more for free shipping!</div>
        <div class="cfb-progress"><div class="cfb-fill" style="width:${progress}%"></div></div>
      </div>`
    : `<div class="cart-free-shipping-earned">✓ You've unlocked free shipping!</div>`;

  if (footer) {
    footer.innerHTML = `
      <div class="cart-subtotal">
        <div class="cart-subtotal__row">
          <span>Subtotal</span>
          <span class="cart-subtotal__amount">${formatMoney(cart.total_price)}</span>
        </div>
        <p class="cart-subtotal__note">Taxes and shipping calculated at checkout</p>
        ${shippingHtml}
      </div>
      <a href="/checkout" class="btn btn-primary btn-full btn-lg cart-checkout-btn">Checkout →</a>
      <button class="btn btn-ghost btn-full" onclick="closeCartDrawer()" type="button" style="margin-top:0.5rem;">Continue Shopping</button>`;
  }
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/* ─── PRODUCT CARD ADD TO CART ───────────────────── */
async function handleCardAddToCart(event) {
  const btn       = event.currentTarget;
  const variantId = btn.dataset.variantId;
  const title     = btn.dataset.title || 'Product';
  const price     = btn.dataset.price || '';
  const original  = btn.textContent.trim();

  btn.textContent = 'Adding…';
  btn.disabled = true;

  try {
    await addToShopifyCart(variantId, 1);
    btn.textContent = '✓ Added!';
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
    }, 2000);

    setStickyBar(title, price, variantId);
  } catch (_) {
    btn.textContent = original;
    btn.disabled = false;
  }
}

/* ─── PRODUCT DETAIL ADD TO CART ─────────────────── */
async function handlePdAddToCart(event) {
  event.preventDefault();
  const btn = document.getElementById('pd-add-btn');
  if (!btn) return;
  const variantId = btn.dataset.variantId;
  const qty = parseInt(document.getElementById('pd-qty')?.value || '1', 10);
  const orig = btn.textContent.trim();

  btn.textContent = 'Adding…';
  btn.disabled = true;

  try {
    await addToShopifyCart(variantId, qty);
    btn.textContent = '✓ Added to Cart!';
    setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2500);
  } catch (_) {
    btn.textContent = orig;
    btn.disabled = false;
  }
}

/* ─── PRODUCT MODAL ───────────────────────────────── */
async function openProductModal(handle) {
  const modal = document.getElementById('product-modal');
  if (!modal) return;

  modal.hidden = false;
  showOverlay();
  setModalLoading(true);

  document.addEventListener('keydown', handleModalEsc);

  const closeBtn = document.getElementById('modal-close-btn');
  if (closeBtn) setTimeout(() => closeBtn.focus(), 100);

  try {
    const res  = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Product not found');
    const p = await res.json();
    populateModal(p);
  } catch (err) {
    closeProductModal();
    showToast('Could not load product details.', 'error');
  } finally {
    setModalLoading(false);
  }
}

function populateModal(p) {
  const variant   = p.variants && p.variants[0];
  const inventory = variant ? variant.inventory_quantity : 0;

  __modalVariantId = variant ? variant.id : null;
  __modalInventory = inventory;

  const el = id => document.getElementById(id);

  if (el('modal-breadcrumb')) el('modal-breadcrumb').textContent = p.title;
  if (el('modal-category'))   el('modal-category').textContent  = p.product_type || '';
  if (el('modal-title'))      el('modal-title').textContent     = p.title;
  if (el('modal-desc'))       el('modal-desc').innerHTML        = p.body_html || '';

  if (el('modal-pricing')) {
    const price   = p.price;
    const compare = p.compare_at_price;
    let html = `<span class="pd-price">${formatMoney(price)}</span>`;
    if (compare && compare > price) {
      const savings = compare - price;
      html += `<span class="pd-compare-price">${formatMoney(compare)}</span>`;
      html += `<span class="pd-save-badge">Save ${formatMoney(savings)}</span>`;
    }
    el('modal-pricing').innerHTML = html;
  }

  if (el('modal-stock')) {
    let dot = 'green', text = `In Stock (${inventory} available)`;
    let textClass = 'stock-text--green';
    if (inventory <= 0)  { dot = 'red';    text = 'Out of Stock';              textClass = 'stock-text--red'; }
    else if (inventory < 20) { dot = 'orange'; text = `Only ${inventory} left in stock!`; textClass = 'stock-text--orange'; }
    el('modal-stock').innerHTML = `<span class="stock-dot stock-dot--${dot}"></span><span class="stock-text ${textClass}">${text}</span>`;
  }

  const addBtn = el('modal-add-btn');
  if (addBtn) {
    addBtn.disabled = inventory <= 0;
    addBtn.textContent = inventory <= 0 ? 'Sold Out' : 'Add to Cart';
  }

  const qtyInput = el('modal-qty');
  if (qtyInput) {
    qtyInput.max = inventory > 0 ? inventory : 99;
    qtyInput.value = '1';
  }

  const mainImg = el('modal-main-image');
  const skeleton = el('modal-image-skeleton');
  const thumbsEl = el('modal-thumbs');

  if (mainImg && p.images && p.images.length) {
    const src = p.images[0].src;
    if (skeleton) skeleton.classList.remove('is-loaded');
    mainImg.onload = () => { if (skeleton) skeleton.classList.add('is-loaded'); };
    mainImg.src = src;
    mainImg.alt = p.title;
  }

  if (thumbsEl) {
    thumbsEl.innerHTML = '';
    if (p.images && p.images.length > 1) {
      p.images.forEach((img, idx) => {
        const btn = document.createElement('button');
        btn.className = `modal-thumb-btn${idx === 0 ? ' is-active' : ''}`;
        btn.type = 'button';
        btn.setAttribute('aria-label', `Product image ${idx + 1}`);
        btn.innerHTML = `<img src="${img.src}" alt="" loading="lazy">`;
        btn.addEventListener('click', () => {
          if (mainImg) { mainImg.src = img.src; mainImg.alt = p.title; }
          thumbsEl.querySelectorAll('.modal-thumb-btn').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
        });
        thumbsEl.appendChild(btn);
      });
    }
  }

  const featuresEl   = el('modal-features');
  const featuresList = el('modal-features-list');
  if (featuresEl && featuresList && p.tags && p.tags.length) {
    const features = p.tags.filter(t => t.startsWith('feature:')).map(t => t.replace('feature:', '').trim());
    if (features.length) {
      featuresList.innerHTML = features.map(f =>
        `<li class="feature-item"><svg class="feature-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>${escapeHtml(f)}</li>`
      ).join('');
      featuresEl.style.display = 'block';
    } else {
      featuresEl.style.display = 'none';
    }
  }
}

function setModalLoading(active) {
  const loadEl  = document.getElementById('modal-loading');
  const layout  = document.querySelector('.product-modal__layout');
  if (loadEl)  loadEl.classList.toggle('is-active', active);
  if (layout)  layout.style.visibility = active ? 'hidden' : 'visible';
}

function closeProductModal() {
  const modal = document.getElementById('product-modal');
  if (!modal) return;
  modal.hidden = true;
  hideOverlay();
  __modalVariantId = null;
  document.removeEventListener('keydown', handleModalEsc);
}

function handleModalEsc(e) {
  if (e.key === 'Escape') closeProductModal();
}

async function modalAddToCart() {
  if (!__modalVariantId) return;
  const btn = document.getElementById('modal-add-btn');
  const qty = parseInt(document.getElementById('modal-qty')?.value || '1', 10);
  const orig = btn ? btn.textContent : 'Add to Cart';

  if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

  try {
    await addToShopifyCart(__modalVariantId, qty);
    if (btn) { btn.textContent = '✓ Added to Cart!'; }
    setTimeout(() => {
      if (btn) { btn.textContent = orig; btn.disabled = __modalInventory <= 0; }
    }, 2000);
  } catch (_) {
    if (btn) { btn.textContent = orig; btn.disabled = false; }
  }
}

function adjustModalQty(delta) {
  const input = document.getElementById('modal-qty');
  if (!input) return;
  const val = parseInt(input.value, 10) + delta;
  const max = parseInt(input.max, 10) || 99;
  input.value = Math.max(1, Math.min(max, val));
}

/* ─── SEARCH ──────────────────────────────────────── */
function toggleSearch() {
  const url = '/search';
  window.location.href = url;
}

/* ─── STICKY CART BAR ─────────────────────────────── */
function initStickyCartBar() {
  const bar   = document.getElementById('sticky-cart-bar');
  const grid  = document.getElementById('product-grid');
  if (!bar || !grid || window.innerWidth >= 900) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting);
      bar.setAttribute('aria-hidden', String(entry.isIntersecting));
    },
    { rootMargin: '0px 0px -60px 0px' }
  );
  observer.observe(grid);

  const firstCard = grid.querySelector('.product-card');
  if (firstCard) {
    setStickyBar(
      firstCard.dataset.title || '',
      firstCard.dataset.price || '',
      firstCard.dataset.variantId
    );
  }
}

function setStickyBar(title, price, variantId) {
  const nameEl  = document.getElementById('sticky-product-name');
  const priceEl = document.getElementById('sticky-product-price');
  if (nameEl)  nameEl.textContent  = title;
  if (priceEl) priceEl.textContent = price;

  window.__stickyAddFn = async () => {
    if (!variantId) return;
    const btn = document.getElementById('sticky-add-btn');
    const orig = btn ? btn.textContent : 'Add to Cart';
    if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }
    try {
      await addToShopifyCart(variantId, 1);
      if (btn) { btn.textContent = '✓ Added!'; }
      setTimeout(() => { if (btn) { btn.textContent = orig; btn.disabled = false; } }, 2000);
    } catch (_) {
      if (btn) { btn.textContent = orig; btn.disabled = false; }
    }
  };
}

/* ─── HERO ANIMATIONS ─────────────────────────────── */
function initHeroAnimations() {
  const hero = document.querySelector('.hero-section');
  if (!hero) return;

  const elements = hero.querySelectorAll('.animate-fade-up');
  elements.forEach(el => {
    const delay = el.style.getPropertyValue('--delay') || el.getAttribute('style')?.match(/--delay:\s*(\d+ms)/)?.[1] || '0ms';
  });
}

/* ─── SMOOTH SCROLL ───────────────────────────────── */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(link => {
    link.addEventListener('click', function (e) {
      const id = this.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  });
}

/* ─── SEARCH TOGGLE ───────────────────────────────── */
function initSearch() {
}

/* ─── DOM READY ───────────────────────────────────── */
document.addEventListener('DOMContentLoaded', function () {
  initAnnouncementBar();
  initSmoothScroll();
  initStickyCartBar();
  initHeroAnimations();

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });

  const cartToggle = document.getElementById('cart-toggle');
  if (cartToggle) {
    cartToggle.addEventListener('click', openCartDrawer);
  }
});

/* ─── EXPOSE GLOBALS ──────────────────────────────── */
window.addToShopifyCart    = addToShopifyCart;
window.updateCartCount     = updateCartCount;
window.addBundleToCart     = addBundleToCart;
window.removeFromCart      = removeFromCart;
window.updateCartQuantity  = updateCartQuantity;
window.openCartDrawer      = openCartDrawer;
window.closeCartDrawer     = closeCartDrawer;
window.openProductModal    = openProductModal;
window.closeProductModal   = closeProductModal;
window.modalAddToCart      = modalAddToCart;
window.adjustModalQty      = adjustModalQty;
window.handleCardAddToCart = handleCardAddToCart;
window.handlePdAddToCart   = handlePdAddToCart;
window.dismissAnnouncement = dismissAnnouncement;
window.toggleMobileMenu    = toggleMobileMenu;
window.closeMobileMenu     = closeMobileMenu;
window.openMobileMenu      = openMobileMenu;
window.closeAll            = closeAll;
window.toggleSearch        = toggleSearch;
window.showToast           = showToast;
